import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  UserIcon,
  CalendarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface DocumentSubmission {
  id: number;
  fileName: string;
  fileSize: number;
  fileType: string;
  gcsFilePath: string;
  gcsBucket: string;
  uploadedAt: string;
  note?: string;
}

interface DocumentRequestItem {
  id: number;
  requestId: number;
  documentTypeId: number;
  status: 'pending' | 'submitted' | 'accepted' | 'rejected';
  submittedAt?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  documentType: {
    name: string;
    description?: string;
    isRequired: boolean;
    maxFileSizeMb: number;
    allowedFileTypes: string[];
  };
  submissions?: DocumentSubmission[];
}

interface DocumentRequest {
  id: number;
  organizationId: number;
  leadId: number;
  requestToken: string;
  status: 'requested' | 'submitted' | 'reviewed' | 'rejected';
  requestedBy: number;
  requestedAt: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: number;
  reviewNotes?: string;
  createdAt: string;
  updatedAt: string;
  lead: {
    firstName: string;
    lastName: string;
    email: string;
  };
  requestedByUser: {
    firstName: string;
    lastName: string;
  };
  reviewedByUser?: {
    firstName: string;
    lastName: string;
  };
  items: DocumentRequestItem[];
}

interface DocumentReviewModalProps {
  request: DocumentRequest;
  onClose: () => void;
  onRequestUpdated: () => void;
}

const DocumentReviewModal: React.FC<DocumentReviewModalProps> = ({ 
  request, 
  onClose, 
  onRequestUpdated 
}) => {
  const [reviewData, setReviewData] = useState<{ [key: number]: { status: 'accepted' | 'rejected', reason?: string } }>({});
  const [overallNotes, setOverallNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<{ url: string; fileName: string; fileType: string } | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newRequestToken, setNewRequestToken] = useState<string>('');

  useEffect(() => {
    // Initialize review data for all items
    const initialData: { [key: number]: { status: 'accepted' | 'rejected', reason?: string } } = {};
    request.items.forEach(item => {
      initialData[item.id] = {
        status: item.status === 'accepted' ? 'accepted' : 'rejected',
        reason: item.rejectionReason || ''
      };
    });
    setReviewData(initialData);
  }, [request]);

  const handleItemReview = (itemId: number, status: 'accepted' | 'rejected', reason?: string) => {
    setReviewData(prev => ({
      ...prev,
      [itemId]: { status, reason: reason || '' }
    }));
  };

  const handleSubmitReview = async () => {
    try {
      setLoading(true);
      setError(null);

      // Separate accepted and rejected items
      const acceptedItems: number[] = [];
      const rejectedItems: { itemId: number; rejectionReason: string }[] = [];

      for (const [itemId, data] of Object.entries(reviewData)) {
        if (data.status === 'accepted') {
          acceptedItems.push(parseInt(itemId));
        } else if (data.status === 'rejected') {
          rejectedItems.push({
            itemId: parseInt(itemId),
            rejectionReason: data.reason || ''
          });
        }
      }

      // If there are rejected items, handle the rejection workflow
      if (rejectedItems.length > 0) {
        // Create new request with rejected documents
        const rejectionResponse = await import('../services/api').then(module => 
          module.default.post(`/documents/requests/${request.id}/handle-rejections`, {
            rejectedItems: rejectedItems
          })
        );

        if (rejectionResponse.data.success) {
          // Show success modal with new request details
          setNewRequestToken(rejectionResponse.data.data.newRequestToken);
          setShowSuccessModal(true);
        }
      }

      // Update accepted items
      for (const itemId of acceptedItems) {
        await import('../services/api').then(module => 
          module.default.put(`/documents/request-items/${itemId}/review`, {
            status: 'accepted'
          })
        );
      }

      // Update overall request status
      const requestStatus = acceptedItems.length > 0 ? 'reviewed' : 'reviewed';
      
      await import('../services/api').then(module => 
        module.default.put(`/documents/requests/${request.id}/status`, {
          status: requestStatus,
          reviewNotes: overallNotes.trim() || undefined
        })
      );

      onRequestUpdated();
      onClose();
    } catch (err: any) {
      console.error('Error submitting review:', err);
      setError(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'rejected':
        return <XCircleIcon className="w-4 h-4 text-red-600" />;
      case 'submitted':
        return <DocumentTextIcon className="w-4 h-4 text-blue-600" />;
      default:
        return <ExclamationTriangleIcon className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handlePreviewFile = async (submission: DocumentSubmission) => {
    try {
      const response = await import('../services/api').then(module => 
        module.default.get(`/documents/files/${encodeURIComponent(submission.fileName)}/download`)
      );
      setPreviewFile({
        url: response.data.data.signedUrl,
        fileName: submission.fileName,
        fileType: submission.fileType
      });
    } catch (error) {
      console.error('Error opening file preview:', error);
    }
  };

  const isPreviewable = (fileType: string) => {
    const previewableTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    return previewableTypes.includes(fileType.toLowerCase());
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Review Documents</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Request Overview */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">
                  {request.lead.firstName} {request.lead.lastName}
                </h4>
                <p className="text-sm text-gray-600">{request.lead.email}</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <p>Submitted on: {formatDate(request.submittedAt!)}</p>
            </div>
          </div>

          {/* Document Items Review */}
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900">Document Review</h4>
            {request.items.map((item) => {
              const currentReview = reviewData[item.id] || { status: 'rejected', reason: '' };
              return (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <h5 className="text-sm font-medium text-gray-900">
                          {item.documentType.name}
                          {item.documentType.isRequired && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Required
                            </span>
                          )}
                        </h5>
                        {item.documentType.description && (
                          <p className="text-xs text-gray-600">{item.documentType.description}</p>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {getStatusIcon(item.status)}
                      <span className="ml-1 capitalize">{item.status}</span>
                    </span>
                  </div>

                  {/* Submitted Files */}
                  {item.submissions && item.submissions.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      <h6 className="text-sm font-medium text-gray-700">Submitted Files:</h6>
                      {item.submissions.map((submission, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{submission.fileName}</p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(submission.fileSize)} • {submission.fileType} • {formatDate(submission.uploadedAt)}
                              </p>
                              {submission.note && (
                                <p className="text-xs text-gray-600 mt-1">Note: {submission.note}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {isPreviewable(submission.fileType) && (
                              <button
                                onClick={() => handlePreviewFile(submission)}
                                className="p-2 text-gray-400 hover:text-gray-600"
                                title="Preview file"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                try {
                                  const response = await import('../services/api').then(module => 
                                    module.default.get(`/documents/files/${encodeURIComponent(submission.fileName)}/download`)
                                  );
                                  window.open(response.data.data.signedUrl, '_blank');
                                } catch (error) {
                                  console.error('Error opening file:', error);
                                }
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600"
                              title="Open in new tab"
                            >
                              <DocumentTextIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const response = await import('../services/api').then(module => 
                                    module.default.get(`/documents/files/${encodeURIComponent(submission.fileName)}/download`)
                                  );
                                  const link = document.createElement('a');
                                  link.href = response.data.data.signedUrl;
                                  link.download = submission.fileName;
                                  link.click();
                                } catch (error) {
                                  console.error('Error downloading file:', error);
                                }
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600"
                              title="Download file"
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                      <p className="text-sm text-yellow-800">No files submitted for this document type</p>
                    </div>
                  )}

                  {/* Review Actions */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`review-${item.id}`}
                          checked={currentReview.status === 'accepted'}
                          onChange={() => handleItemReview(item.id, 'accepted')}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Accept</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`review-${item.id}`}
                          checked={currentReview.status === 'rejected'}
                          onChange={() => handleItemReview(item.id, 'rejected')}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Reject</span>
                      </label>
                    </div>

                    {currentReview.status === 'rejected' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rejection Reason *
                        </label>
                        <textarea
                          value={currentReview.reason}
                          onChange={(e) => handleItemReview(item.id, 'rejected', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                          placeholder="Please provide a reason for rejection..."
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overall Review Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Overall Review Notes (Optional)
            </label>
            <textarea
              value={overallNotes}
              onChange={(e) => setOverallNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any overall comments about this document submission..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitReview}
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting Review...
                </div>
              ) : (
                'Submit Review'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* File Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">{previewFile.fileName}</h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4">
              {previewFile.fileType.startsWith('image/') ? (
                <img
                  src={previewFile.url}
                  alt={previewFile.fileName}
                  className="max-w-full max-h-[70vh] mx-auto"
                />
              ) : previewFile.fileType === 'application/pdf' ? (
                <iframe
                  src={previewFile.url}
                  className="w-full h-[70vh] border-0"
                  title={previewFile.fileName}
                />
              ) : (
                <div className="text-center py-8">
                  <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Preview not available for this file type</p>
                  <button
                    onClick={() => window.open(previewFile.url, '_blank')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Open in New Tab
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Modal for Rejected Documents */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Rejected Documents Moved
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  Rejected documents have been moved to a new request. The lead will need to resubmit these documents.
                </p>
                <div className="bg-gray-50 p-3 rounded-md mb-4">
                  <p className="text-xs text-gray-600 mb-1">New Request Token:</p>
                  <p className="text-sm font-mono text-gray-800 break-all">{newRequestToken}</p>
                </div>
                <p className="text-xs text-gray-500">
                  You can share this token with the lead to allow them to resubmit the rejected documents.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setNewRequestToken('');
                  }}
                  className="px-4 py-2 bg-blue-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentReviewModal;
